// Imported from the modules directly rather than the `generic` barrel — see the
// note in `chartSpecs.ts`.
import { seriesColorAt } from '@/components/charts/generic/constants';
import type { MetricSeriesSpec } from '@/components/charts/generic/seriesTypes';
import { graphSettings } from '@/config/settings';
import type { DomainOverviewDay } from '@/domain/cages/overview.types';

/**
 * Colour for a daypart tag, from the app-wide graph settings so the new charts
 * keep the palette the legacy ones used (`all` green, `day` orange, `night`
 * purple, `short_day` pink).
 */
export function daypartColor(daypartTag: string, fallbackIndex = 0): string {
    const colors = graphSettings.daytagColors as Record<string, string | undefined>;
    return colors[daypartTag] ?? seriesColorAt(fallbackIndex);
}

export interface DaypartMetricSeriesParams<TMetrics> {
    /** Rows from any one overview block — they share the same envelope. */
    days: DomainOverviewDay<TMetrics>[];
    /** Which metric of `day.data` to plot. Type-checked against that block's shape. */
    metric: keyof TMetrics;
    /**
     * Dayparts that get a series at all — from `filterGraphDayparts`, which gates
     * on cage type, admin rights and the LIVING_WEIGHT_ALL_TAGS_ENABLED flag.
     */
    graphedDaypartTags: string[];
    /**
     * Dayparts that start visible. The others are still in the legend, matching
     * the legacy charts, which passed the non-selected tags as `inactiveSetLegend`
     * so only the daypart chosen in the page filter was shown up front.
     */
    activeDaypartTags: string[];
    /** Already translated, e.g. `(tag) => t(\`daypartTag.${tag}\`)`. */
    label: (daypartTag: string) => string;
    /** Prefixes the series key so several metrics can share one chart. */
    keyPrefix: string;
    axis?: 'left' | 'right';
    /** `false` plots points without connecting them. Defaults to a line. */
    showLine?: boolean;
}

/**
 * Fans one metric out into a series per daypart tag.
 *
 * Built here rather than with the generic `seriesPerDaypart` helper because these
 * charts need the app's daypart palette and the active/inactive split, neither of
 * which belongs in the shared chart layer.
 */
export function daypartMetricSeries<TMetrics extends Record<string, number | null>>(
    params: DaypartMetricSeriesParams<TMetrics>
): MetricSeriesSpec[] {
    const {
        days,
        metric,
        graphedDaypartTags,
        activeDaypartTags,
        label,
        keyPrefix,
        axis,
        showLine,
    } = params;

    return graphedDaypartTags.map((daypartTag, index) => ({
        key: `${keyPrefix}:${daypartTag}`,
        label: label(daypartTag),
        color: daypartColor(daypartTag, index),
        axis,
        showLine,
        hiddenByDefault: !activeDaypartTags.includes(daypartTag),
        data: days
            .filter((day) => day.daypartTag === daypartTag)
            .map((day) => ({ day: day.day, value: day.data[metric] })),
    }));
}

/**
 * The daypart whose trend line the living-weight chart draws.
 *
 * The legacy mapper picked the first *visible* daypart, so the trend follows
 * whichever series the page filter has selected rather than being pinned to one
 * tag. Reproduced here.
 */
export function trendDaypartTag(
    graphedDaypartTags: string[],
    activeDaypartTags: string[]
): string | undefined {
    return graphedDaypartTags.find((daypartTag) => activeDaypartTags.includes(daypartTag));
}
